import { useState, useMemo, useRef } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Clock,
  Calendar as CalendarIcon,
  Repeat,
  Loader2,
  X,
} from 'lucide-react';
import { Button } from './ui/button';
import { Program } from '../data/mockData';
import { API_BASE_URL, useListProgramsQuery } from '../../store/api';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  startOfDay,
  addDays,
  addMonths,
  subMonths,
  isSameMonth,
  isSameDay,
  isToday,
} from 'date-fns';
import { parseApiDate } from '../utils/dateTime';
import { ProgramDetailPage } from './ProgramDetailPage';

interface ChannelCalendarTabProps {
  channelId: string | number;
}

export function ChannelCalendarTab({ channelId }: ChannelCalendarTabProps) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedProgramId, setSelectedProgramId] = useState<string | null>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  const { data: rawData, isFetching } = useListProgramsQuery(Number(channelId));

  const programs: Program[] = useMemo(() => {
    return (rawData ?? []).map((p: any) => {
      const rawTrackId = p.trackId ?? p.track_id;
      return {
        ...p,
        id: String(p.id),
        channelId: String(p.channelId ?? p.channel_id),
        trackId: rawTrackId != null ? String(rawTrackId) : null,
      };
    });
  }, [rawData]);

  // Build a map: "YYYY-MM-DD" → Program[]
  // Programs spanning midnight appear on BOTH start-date and end-date
  const programsByDate = useMemo(() => {
    const map = new Map<string, Program[]>();
    const addToDay = (key: string, p: Program) => {
      const list = map.get(key) ?? [];
      if (!list.some((x) => String(x.id) === String(p.id))) {
        list.push(p);
      }
      map.set(key, list);
    };
    for (const p of programs) {
      const start = parseApiDate(p.startDateTime);
      const end = parseApiDate(p.endDateTime);
      if (isNaN(start.getTime()) || isNaN(end.getTime())) continue;

      // Add to start date
      addToDay(format(start, 'yyyy-MM-dd'), p);

      // If end date is on a different day, add to every day it spans
      let cursor = addDays(startOfDay(start), 1);
      const endDay = startOfDay(end);
      while (cursor <= endDay) {
        addToDay(format(cursor, 'yyyy-MM-dd'), p);
        cursor = addDays(cursor, 1);
      }
    }
    return map;
  }, [programs]);

  // Calendar grid
  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const gridStart = startOfWeek(monthStart, { weekStartsOn: 0 });
    const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });

    const days: Date[] = [];
    let day = gridStart;
    while (day <= gridEnd) {
      days.push(day);
      day = addDays(day, 1);
    }
    return days;
  }, [currentMonth]);

  const weeks = useMemo(() => {
    const result: Date[][] = [];
    for (let i = 0; i < calendarDays.length; i += 7) {
      result.push(calendarDays.slice(i, i + 7));
    }
    return result;
  }, [calendarDays]);

  // Programs for selected date, sorted by time
  const selectedDatePrograms = useMemo(() => {
    if (!selectedDate) return [];
    const key = format(selectedDate, 'yyyy-MM-dd');
    return (programsByDate.get(key) ?? []).sort((a, b) => {
      return parseApiDate(a.startDateTime).getTime() - parseApiDate(b.startDateTime).getTime();
    });
  }, [selectedDate, programsByDate]);

  const colorVariants = [
    { bg: 'bg-blue-600/20', text: 'text-blue-200', dot: 'bg-blue-500' },
    { bg: 'bg-emerald-600/20', text: 'text-emerald-200', dot: 'bg-emerald-500' },
    { bg: 'bg-violet-600/20', text: 'text-violet-200', dot: 'bg-violet-500' },
    { bg: 'bg-amber-600/20', text: 'text-amber-200', dot: 'bg-amber-500' },
    { bg: 'bg-rose-600/20', text: 'text-rose-200', dot: 'bg-rose-500' },
    { bg: 'bg-cyan-600/20', text: 'text-cyan-200', dot: 'bg-cyan-500' },
  ];

  const getProgramColor = (program: Program) => {
    const key = `${String(program.id)}-${program.title}`;
    let hash = 0;
    for (let i = 0; i < key.length; i++) {
      hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
    }
    return colorVariants[hash % colorVariants.length];
  };

  const handleDateClick = (day: Date) => {
    if (selectedDate && isSameDay(day, selectedDate)) {
      setSelectedDate(null);
      return;
    }
    setSelectedDate(day);
  };

  if (selectedProgramId) {
    return (
      <ProgramDetailPage
        programId={selectedProgramId}
        channelId={channelId}
        onBack={() => setSelectedProgramId(null)}
      />
    );
  }

  if (!rawData && isFetching) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="size-8 animate-spin text-gray-500" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
      {/* Navigation bar */}
      <div className="flex items-center justify-between py-3 mb-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
          className="text-gray-400 hover:text-white hover:bg-gray-800"
        >
          <ChevronLeft className="size-5" />
        </Button>
        <button
          onClick={() => setCurrentMonth(new Date())}
          className="px-4 py-1.5 text-base font-semibold text-white hover:bg-gray-800 rounded-lg transition-colors min-w-[180px] text-center"
        >
          {format(currentMonth, 'MMMM yyyy')}
        </button>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
          className="text-gray-400 hover:text-white hover:bg-gray-800"
        >
          <ChevronRight className="size-5" />
        </Button>
      </div>

      {/* Calendar grid */}
      <div className="rounded-xl border border-gray-700 bg-gray-800/50 overflow-hidden">
        {/* Day-of-week headers */}
        <div className="grid grid-cols-7 border-b border-gray-700 bg-gray-800">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
            <div key={d} className="py-2 text-center text-xs font-semibold text-gray-400 uppercase tracking-wider">
              {d}
            </div>
          ))}
        </div>

        {/* Date rows */}
        <div className="grid" style={{ gridTemplateRows: `repeat(${weeks.length}, minmax(0, 1fr))` }}>
          {weeks.map((week, wIdx) => (
            <div key={wIdx} className="grid grid-cols-7 min-h-0">
              {week.map((day, dIdx) => {
                const key = format(day, 'yyyy-MM-dd');
                const dayPrograms = programsByDate.get(key) ?? [];
                const inMonth = isSameMonth(day, currentMonth);
                const todayFlag = isToday(day);
                const isSelected = selectedDate ? isSameDay(day, selectedDate) : false;

                return (
                  <button
                    key={dIdx}
                    onClick={() => handleDateClick(day)}
                    className={`relative min-h-[100px] p-1.5 border-b border-r border-gray-700/50 text-left transition-colors overflow-hidden flex flex-col
                      ${!inMonth ? 'bg-gray-900/40' : 'bg-transparent hover:bg-gray-700/20'}
                      ${isSelected ? 'ring-2 ring-inset ring-blue-500 bg-blue-500/10' : ''}
                    `}
                  >
                    {/* Day number */}
                    <span
                      className={`inline-flex items-center justify-center size-6 rounded-full text-xs font-medium flex-shrink-0 mb-0.5
                        ${todayFlag ? 'bg-blue-600 text-white' : ''}
                        ${!todayFlag && inMonth ? 'text-gray-200' : ''}
                        ${!todayFlag && !inMonth ? 'text-gray-600' : ''}
                      `}
                    >
                      {format(day, 'd')}
                    </span>

                    {/* Program chips — name + time */}
                    {dayPrograms.length > 0 && (
                      <div className="flex-1 min-h-0 overflow-hidden space-y-0.5">
                        {dayPrograms.slice(0, 3).map((prog) => {
                          const color = getProgramColor(prog);
                          const start = parseApiDate(prog.startDateTime);
                          return (
                            <div
                              key={String(prog.id)}
                              className={`${color.bg} ${color.text} rounded px-1 py-px`}
                            >
                              <div className="text-[10px] font-medium leading-tight truncate">
                                {prog.title}
                              </div>
                              <div className="text-[9px] opacity-70 leading-tight">
                                {format(start, 'h:mm a')}
                              </div>
                            </div>
                          );
                        })}
                        {dayPrograms.length > 3 && (
                          <span className="text-[10px] text-gray-400 font-medium pl-0.5">
                            +{dayPrograms.length - 3} more
                          </span>
                        )}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Centered popup overlay */}
      {selectedDate && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
          onClick={() => setSelectedDate(null)}
        >
        <div
          ref={popupRef}
          className="w-[400px] max-h-[500px] overflow-y-auto rounded-xl border border-gray-600 bg-gray-900 shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-0 flex items-center justify-between px-4 py-3 border-b border-gray-700 bg-gray-900 z-10">
            <div>
              <h3 className="text-base font-bold text-white">
                {format(selectedDate, 'EEEE, MMMM d')}
              </h3>
              <p className="text-xs text-gray-400">
                {selectedDatePrograms.length} program{selectedDatePrograms.length !== 1 ? 's' : ''}
              </p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSelectedDate(null)}
              className="text-gray-400 hover:text-white hover:bg-gray-700 size-7"
            >
              <X className="size-3.5" />
            </Button>
          </div>

          {/* Programs */}
          <div className="p-3 space-y-2">
            {selectedDatePrograms.length === 0 ? (
              <div className="flex flex-col items-center py-8 text-center">
                <CalendarIcon className="size-8 text-gray-600 mb-2" />
                <p className="text-sm text-gray-400">No programs</p>
              </div>
            ) : (
              selectedDatePrograms.map((program) => {
                const start = parseApiDate(program.startDateTime);
                const end = parseApiDate(program.endDateTime);
                const thumb = program.thumbnail
                  ? program.thumbnail.startsWith('/')
                    ? `${API_BASE_URL}${program.thumbnail}`
                    : program.thumbnail
                  : null;

                return (
                  <button
                    key={String(program.id)}
                    onClick={() => { setSelectedProgramId(String(program.id)); setSelectedDate(null); }}
                    className="w-full text-left rounded-lg border border-gray-700 bg-gray-800/80 hover:bg-gray-700/80 transition-colors overflow-hidden group"
                  >
                    {thumb && (
                      <img src={thumb} alt={program.title} loading="lazy" className="w-full h-24 object-cover" />
                    )}
                    <div className="p-2.5">
                      <h4 className="text-sm font-semibold text-white leading-tight group-hover:text-blue-400 transition-colors mb-1">
                        {program.title}
                      </h4>
                      <div className="flex items-center gap-1.5 text-xs text-gray-400">
                        <Clock className="size-3 flex-shrink-0" />
                        <span>{format(start, 'h:mm a')} – {format(end, 'h:mm a')}</span>
                      </div>
                      {program.description && (
                        <p className="text-xs text-gray-400 mt-1.5 line-clamp-2 leading-relaxed">
                          {program.description}
                        </p>
                      )}
                      {program.scheduleType === 'recurring' && (
                        <div className="flex items-center gap-1 text-xs text-purple-400 mt-1">
                          <Repeat className="size-3 flex-shrink-0" />
                          <span>
                            {program.recurrence?.frequency === 'daily' ? 'Daily' : 'Weekly'}
                            {program.recurrence?.frequency === 'weekly' &&
                              program.recurrence.daysOfWeek &&
                              program.recurrence.daysOfWeek.length > 0 &&
                              ` · ${program.recurrence.daysOfWeek.slice().sort((a: number, b: number) => a - b).map((d: number) => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d]).join(', ')}`}
                          </span>
                        </div>
                      )}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
        </div>
      )}
      </div>
    </div>
  );
}
